import { supabase } from '../integrations/supabase/client.ts';
import type { Product } from '../types/product.ts';

interface PaginatedProductsResponse {
  products: Product[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasMore: boolean;
}

interface ProductsFilters {
  status?: string;
  searchQuery?: string;
  includeSubscriptions?: boolean;
  includeOneTime?: boolean;
  billing_period?: string;
}

export const fetchPaginatedProducts = async (
  page: number = 1,
  limit: number = 10,
  filters: ProductsFilters = {}
): Promise<PaginatedProductsResponse> => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user?.id) {
      console.error('No authenticated user found:', authError);
      return {
        products: [],
        totalCount: 0,
        totalPages: 0,
        currentPage: page,
        hasMore: false
      };
    }

    const offset = (page - 1) * limit;
    
    let query = supabase
      .from('products')
      .select('*', { count: 'exact' })
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (filters.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }

    if (filters.billing_period) {
      if (filters.billing_period === 'one_time') {
        query = query.eq('billing_period', 'one_time');
      } else if (filters.billing_period === 'subscription') {
        query = query.neq('billing_period', 'one_time').neq('billing_period', null);
      }
    } else {
      if (filters.includeSubscriptions === false && filters.includeOneTime === true) {
        query = query.eq('billing_period', 'one_time');
      } else if (filters.includeSubscriptions === true && filters.includeOneTime === false) {
        query = query.neq('billing_period', 'one_time').neq('billing_period', null);
      }
    }

    if (filters.searchQuery && filters.searchQuery.trim() !== '') {
      const searchTerm = filters.searchQuery.toLowerCase().trim();
      query = query.or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,id.ilike.%${searchTerm}%`);
    }

    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching paginated products:', error);
      throw error;
    }

    const products: Product[] = (data || []).map(item => ({
      id: item.id,
      title: item.name,
      name: item.name,
      description: item.description || '',
      amount: item.price_cents || 0,
      price_cents: item.price_cents || 0,
      status: item.status || 'active',
      billing_period: item.billing_period || 'one_time',
      created_at: item.created_at,
      user_id: item.user_id,
      stripe_product_id: item.stripe_product_id,
      stripe_price_id: item.stripe_price_id,
      currency: item.currency,
      recurring_period: item.recurring_period,
      is_subscription: item.is_subscription,
      use_anchor_billing: item.use_anchor_billing,
      anchor_billing_day: item.anchor_billing_day,
      is_test: item.is_test,
      hero_image: item.hero_image
    }));

    const totalCount = count || 0;
    const totalPages = Math.ceil(totalCount / limit);
    const hasMore = page < totalPages;

    return {
      products,
      totalCount,
      totalPages,
      currentPage: page,
      hasMore
    };

  } catch (error) {
    console.error('Error in fetchPaginatedProducts:', error);
    return {
      products: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: page,
      hasMore: false
    };
  }
};

export type { ProductsFilters, PaginatedProductsResponse };
